import { type ReactNode, useState } from 'react';
import type { TypeTokens } from '../tokens';

export interface Footnote {
  id: string;
  label: string;
  /** Shown beside the label so a reader knows the size of what they are opening. */
  count?: number;
  children: ReactNode;
}

interface FootnotesProps {
  type: Pick<TypeTokens, 'meta' | 'metaValue'>;
  items: Footnote[];
}

/**
 * The apparatus behind a finding, kept folded away until asked for and then shown one panel at a
 * time.
 *
 * What it rests on, what else could explain it and where the figures came from are all real and all
 * long. Opened independently they piled up: a reader who wanted the reasoning and then glanced at the
 * sources ended up several screens tall with the action they came for far above them. One at a time
 * gives the expansion a shape that does not depend on how much has been clicked.
 *
 * The chevron stays. Without it a strip of labels with nothing open reads as three inert words rather
 * than as three things a reader can open.
 */
export const Footnotes = ({ type, items }: FootnotesProps) => {
  const [open, setOpen] = useState<string>();
  const present = items.filter((item) => item.count === undefined || item.count > 0);
  const openItem = present.find((item) => item.id === open);

  if (present.length === 0) return null;

  return (
    <div className="flex flex-col border-t border-border-neutral-subtle pt-4">
      <div
        role="tablist"
        className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border-neutral-subtle"
      >
        {present.map((item) => {
          const isOpen = item.id === openItem?.id;

          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={isOpen}
              aria-expanded={isOpen}
              data-testid={`agentic-x-footnote-${item.id}`}
              onClick={() => setOpen(isOpen ? undefined : item.id)}
              // The brand bar overlaps the strip's hairline, so the open panel reads as hanging off
              // the label that opened it rather than as a block that happens to be below.
              className={`${type.meta} -mb-px flex items-center gap-1.5 border-b-2 pb-2 transition-colors ${
                isOpen
                  ? 'border-b-border-brand text-text-neutral-primary'
                  : 'border-b-transparent hover:text-text-neutral-primary'
              }`}
            >
              <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
              {item.label}
              {item.count !== undefined ? (
                <span className={type.metaValue}>{item.count}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {openItem ? (
        <section
          role="tabpanel"
          data-testid={`agentic-x-footnote-body-${openItem.id}`}
          className="flex flex-col gap-3 pt-5"
        >
          {/* No heading of its own: the label that opened it is directly above and still marked. */}
          {openItem.children}
        </section>
      ) : null}
    </div>
  );
};
